import { useMutation, useQueryClient } from 'react-query';

import { APIBaseChronos } from '@/api/hosts';
import type { KimThread } from '@/api/queries/useGetKimThreads';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

export const useCreateKimThread = (matterId?: string, subsetId?: string) => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();
  const queryKey = [QueryEntity.KimThreads, { subsetId }];

  return useMutation(
    async (message: string) => {
      const fetchConfig = getFetchConfig({
        method: FetchMethod.POST,
        data: { message },
      });

      const response = await fetch(
        `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/kim/thread`,
        fetchConfig,
      );
      if (!response.ok) {
        throw new Error('Creating Kim thread failed');
      }
      return response.json();
    },
    {
      onMutate: async (message: string) => {
        await queryClient.cancelQueries(queryKey);
        const previous = queryClient.getQueryData<KimThread[]>(queryKey);

        const optimisticThread: KimThread = {
          id: `optimistic-${Date.now()}`,
          name: message.replace(/@\[([^\]]+)\]\([^)]+\)/g, '$1').slice(0, 200),
          createdDate: new Date().toISOString(),
        };

        queryClient.setQueryData<KimThread[]>(queryKey, (old) => [optimisticThread, ...(old ?? [])]);
        return { previous };
      },
      onSuccess: () => {
        setTimeout(() => queryClient.invalidateQueries(queryKey), 3000);
      },
      onError: (_err, _message, context) => {
        if (context?.previous) {
          queryClient.setQueryData(queryKey, context.previous);
        }
      },
    },
  );
};
